import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Compass, Target, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const ABOUT_IMG =
  "https://images.pexels.com/photos/36765719/pexels-photo-36765719.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const About = () => {
  const { dict } = useI18n();
  const a = dict.about;

  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-5"
        >
          <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
            {a.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.08] text-[hsl(var(--primary))]">
            {a.title}
          </h2>
          <p className="mt-5 text-slate-700 leading-relaxed">{a.lead}</p>

          <div className="mt-7 grid grid-cols-3 gap-3">
            {a.values.map((v) => (
              <div
                key={v.title}
                className="rounded-xl border border-slate-200 bg-white p-3.5"
                data-testid={`about-value-${v.title}`}
              >
                <div className="flex items-center gap-1.5 text-[hsl(var(--accent))]">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--primary))]">
                    {v.title}
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-600 leading-snug">{v.body}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200">
            <img src={ABOUT_IMG} alt="Team meeting" className="w-full h-64 object-cover" loading="lazy" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          <Card
            data-testid="about-mission-card"
            className="card-lift bg-white border border-slate-200 rounded-2xl p-7"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white">
              <Target className="h-5 w-5" />
            </div>
            <h3 className="mt-5 font-display text-2xl text-[hsl(var(--primary))]">{a.missionTitle}</h3>
            <p className="mt-3 text-sm md:text-[15px] text-slate-700 leading-relaxed">{a.missionBody}</p>
            <div className="gold-rule mt-6" />
          </Card>

          <Card
            data-testid="about-vision-card"
            className="card-lift bg-white border border-slate-200 rounded-2xl p-7"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white">
              <Compass className="h-5 w-5" />
            </div>
            <h3 className="mt-5 font-display text-2xl text-[hsl(var(--primary))]">{a.visionTitle}</h3>
            <p className="mt-3 text-sm md:text-[15px] text-slate-700 leading-relaxed">{a.visionBody}</p>
            <div className="gold-rule mt-6" />
          </Card>

          <Card className="md:col-span-2 bg-[hsl(var(--primary))] text-white rounded-2xl p-7 border-0 relative overflow-hidden">
            <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-[hsl(var(--accent))]/15" />
            <div className="relative">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
                TBS · GROUP
              </div>
              <h3 className="mt-2 font-display text-2xl md:text-3xl leading-tight">
                {dict.hero.headline}
              </h3>
              <p className="mt-3 max-w-2xl text-sm md:text-[15px] text-white/80 leading-relaxed">
                {dict.hero.subhead}
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
